import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStore } from "../../store/useStore";

const Compliance: React.FC = () => {
  const { t } = useTranslation();
  const { patients, employees, invoices, suppliers, assets } = useStore();

  // Calculate compliance metrics from store data
  const totalPatients = patients.length;
  const totalEmployees = employees.length;
  const totalInvoices = invoices.length;
  const totalSuppliers = suppliers.length;
  const totalAssets = assets.length;

  // Calculate overall compliance score based on data completeness
  const dataCompleteness = {
    patients: totalPatients > 0 ? 100 : 0,
    employees: totalEmployees > 0 ? 100 : 0,
    invoices: totalInvoices > 0 ? 100 : 0,
    suppliers: totalSuppliers > 0 ? 100 : 0,
    assets: totalAssets > 0 ? 100 : 0,
  };

  const overallScore =
    Object.values(dataCompleteness).reduce((sum, score) => sum + score, 0) /
    Object.keys(dataCompleteness).length;

  const complianceData = [
    {
      area: "HIPAA Compliance",
      status: "Compliant",
      lastAudit: "2024-01-15",
      nextAudit: "2024-07-15",
      score: 95,
    },
    {
      area: "GDPR Compliance",
      status: "Compliant",
      lastAudit: "2024-01-10",
      nextAudit: "2024-07-10",
      score: 92,
    },
    {
      area: "MOH Regulations",
      status: "Compliant",
      lastAudit: "2024-01-20",
      nextAudit: "2024-07-20",
      score: 98,
    },
    {
      area: "ISO 27001",
      status: "In Progress",
      lastAudit: "2023-12-15",
      nextAudit: "2024-06-15",
      score: 85,
    },
    {
      area: "Data Management",
      status:
        overallScore >= 90
          ? "Compliant"
          : overallScore >= 70
          ? "In Progress"
          : "Non-Compliant",
      lastAudit: "2024-01-25",
      nextAudit: "2024-07-25",
      score: Math.round(overallScore),
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Compliant":
        return "success";
      case "In Progress":
        return "warning";
      case "Non-Compliant":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t("compliance.title")}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Regulatory compliance and audit management
      </Typography>

      {/* Compliance Overview */}
      <Grid container spacing={3} sx={{ mb: 3, width: "100%" }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Overall Compliance Score
              </Typography>
              <Typography variant="h4" color="primary">
                {Math.round(overallScore)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={overallScore}
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Audits
              </Typography>
              <Typography variant="h4" color="warning.main">
                3
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Compliance Issues
              </Typography>
              <Typography variant="h4" color="error.main">
                2
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Certifications
              </Typography>
              <Typography variant="h4" color="success.main">
                8
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Compliance Details */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Compliance Status
          </Typography>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Compliance Area</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Last Audit</TableCell>
                  <TableCell>Next Audit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {complianceData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.area}</TableCell>
                    <TableCell>
                      <Chip
                        label={item.status}
                        color={getStatusColor(item.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <LinearProgress
                          variant="determinate"
                          value={item.score}
                          sx={{ width: 100, mr: 2 }}
                        />
                        <Typography variant="body2">{item.score}%</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{item.lastAudit}</TableCell>
                    <TableCell>{item.nextAudit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Compliance;
